import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ExpandingSearch } from "@/components/ui/expanding-search";
import { SearchChip } from "@/components/ui/search-chip";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, FileText, Briefcase } from "lucide-react";
import { ChipFilter, ChipFilterOption } from "@/components/ui/chip-filter";
import { AddFilterMenu } from "@/components/ui/add-filter-menu";
import { useCallback, useRef } from "react";
import { AddressFields, formatAddress } from "@/components/forms/AddressFields";
import { Customer } from "@/types/tms";
import { useCRUDMutations } from "@/hooks/useCRUDMutations";
import { ConfirmDeleteButton } from "@/components/ui/confirm-delete-button";
import { BulkActionBar } from "@/components/ui/bulk-action-bar";
import { useBulkSelection } from "@/hooks/useBulkSelection";
import { toast } from "sonner";
import { ResponsiveTableShell } from "@/components/ui/responsive-table-shell";
import { TablePagination } from "@/components/ui/table-pagination";
import { useTablePagination } from "@/hooks/useTablePagination";
import { CustomerCard } from "@/components/customers/CustomerCard";

export default function Customers() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [billingReqFilter, setBillingReqFilter] = useState<string[]>(["all"]);
  const [operationsFilter, setOperationsFilter] = useState<string[]>(["all"]);

  type FilterKey = "billing" | "operations";
  const isActive = (v: string[]) => v.length > 0 && !v.includes("all");
  const [activeFilters, setActiveFilters] = useState<FilterKey[]>(() => {
    const init: FilterKey[] = [];
    if (isActive(billingReqFilter)) init.push("billing");
    if (isActive(operationsFilter)) init.push("operations");
    return init;
  });
  const [openFilterKey, setOpenFilterKey] = useState<FilterKey | null>(null);
  const ignoreAutoCloseRef = useRef<FilterKey | null>(null);

  const addFilter = useCallback((key: string) => {
    const fk = key as FilterKey;
    setActiveFilters((prev) => (prev.includes(fk) ? prev : [...prev, fk]));
    window.setTimeout(() => {
      ignoreAutoCloseRef.current = fk;
      setOpenFilterKey(fk);
      window.setTimeout(() => {
        if (ignoreAutoCloseRef.current === fk) ignoreAutoCloseRef.current = null;
      }, 250);
    }, 50);
  }, []);

  const removeFilter = useCallback((key: FilterKey) => {
    setActiveFilters((prev) => prev.filter((k) => k !== key));
    setOpenFilterKey((prev) => (prev === key ? null : prev));
    if (ignoreAutoCloseRef.current === key) ignoreAutoCloseRef.current = null;
    if (key === "billing") setBillingReqFilter(["all"]);
    if (key === "operations") setOperationsFilter(["all"]);
  }, []);

  const allCategories: Array<{ key: FilterKey; label: string; icon: React.ReactNode }> = [
    { key: "billing", label: "Billing requirements", icon: <FileText className="h-3.5 w-3.5" /> },
    { key: "operations", label: "Operations", icon: <Briefcase className="h-3.5 w-3.5" /> },
  ];
  const availableCategories = allCategories.filter((c) => !activeFilters.includes(c.key));

  const handleOpenChange = (key: FilterKey, isOpen: boolean, currentValue: string[]) => {
    if (!isOpen && ignoreAutoCloseRef.current === key) {
      ignoreAutoCloseRef.current = null;
      return;
    }
    if (!isOpen) {
      const cleared = currentValue.length === 0 || currentValue.every((x) => x === "all");
      if (cleared) setActiveFilters((prev) => prev.filter((k) => k !== key));
      setOpenFilterKey((prev) => (prev === key ? null : prev));
    } else {
      setOpenFilterKey(key);
    }
  };
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [formData, setFormData] = useState({
    organization_name: "",
    short_code: "",
    email: "",
    street_address: "",
    city: "",
    province_state: "",
    postal_zip_code: "",
    country: "",
    requires_tender_id: false,
    requires_bol: false,
    requires_tender: false,
    authorized_nvo: false,
  });

  const { data: customers, isLoading } = useQuery({
    queryKey: ["customers"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("customers")
        .select("*")
        .order("organization_name");
      if (error) throw error;
      return data as Customer[];
    },
  });

  const { data: templateCounts } = useQuery({
    queryKey: ["recurring-template-counts"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("recurring_order_templates")
        .select("billing_party_id");
      if (error) throw error;
      const counts: Record<string, number> = {};
      for (const row of data) {
        if (row.billing_party_id) {
          counts[row.billing_party_id] = (counts[row.billing_party_id] || 0) + 1;
        }
      }
      return counts;
    },
  });

  const billingActive = isActive(billingReqFilter);
  const operationsActive = isActive(operationsFilter);

  const filteredCustomers = customers?.filter((c) => {
    if (
      searchQuery &&
      !c.organization_name.toLowerCase().includes(searchQuery.toLowerCase()) &&
      !c.short_code.toLowerCase().includes(searchQuery.toLowerCase())
    ) {
      return false;
    }
    if (billingActive) {
      const matches = billingReqFilter.some((req) => {
        if (req === "tender") return c.requires_tender;
        if (req === "tender_id") return c.requires_tender_id;
        if (req === "bol") return c.requires_bol;
        return false;
      });
      if (!matches) return false;
    }
    if (operationsActive) {
      const matches = operationsFilter.some((op) => {
        if (op === "nvo") return c.authorized_nvo;
        return false;
      });
      if (!matches) return false;
    }
    return true;
  });

  const { selectedIds, toggle, toggleAll, clear, allSelected, someSelected, count } = useBulkSelection(filteredCustomers);


  const {
    page,
    setPage,
    pageSize,
    setPageSize,
    totalPages,
    totalCount,
    pageRows: pagedCustomers,
  } = useTablePagination(filteredCustomers);

  const resetForm = () => {
    setFormData({
      organization_name: "", short_code: "", email: "", street_address: "", city: "",
      province_state: "", postal_zip_code: "", country: "",
      requires_tender_id: false, requires_bol: false, requires_tender: false, authorized_nvo: false,
    });
    setEditingCustomer(null);
    setIsDialogOpen(false);
  };

  const { createMutation, updateMutation, deleteMutation } = useCRUDMutations<typeof formData>({
    tableName: "customers",
    queryKey: "customers",
    entityLabel: "Customer",
    onCreateSuccess: resetForm,
    onUpdateSuccess: resetForm,
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const { error } = await supabase.from("customers").delete().in("id", ids);
      if (error) throw error;
      return ids.length;
    },
    onSuccess: (count) => {
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      toast.success(`Deleted ${count} customer(s)`);
      clear();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const handleEdit = (customer: Customer) => {
    setEditingCustomer(customer);
    setFormData({
      organization_name: customer.organization_name,
      short_code: customer.short_code,
      email: customer.email || "",
      street_address: customer.street_address || "",
      city: customer.city || "",
      province_state: customer.province_state || "",
      postal_zip_code: customer.postal_zip_code || "",
      country: customer.country || "",
      requires_tender_id: customer.requires_tender_id,
      requires_bol: customer.requires_bol,
      requires_tender: customer.requires_tender,
      authorized_nvo: customer.authorized_nvo,
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload = { ...formData, email: formData.email.trim() || null };
    if (editingCustomer) {
      updateMutation.mutate({ id: editingCustomer.id, data: payload as typeof formData });
    } else {
      createMutation.mutate(payload as typeof formData);
    }
  };

  const openCustomer = (id: string) => navigate(`/customers/${id}`);

  return (
    <AppLayout
      title="Customers"
      actions={
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button><Plus className="h-4 w-4 mr-2" /> Add Customer</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingCustomer ? "Edit Customer" : "Add Customer"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="org-name">Organization Name *</Label>
                  <Input id="org-name" value={formData.organization_name} onChange={(e) => setFormData({ ...formData, organization_name: e.target.value })} required />
                </div>
                <div>
                  <Label htmlFor="short-code">Short Code *</Label>
                  <Input id="short-code" value={formData.short_code} onChange={(e) => setFormData({ ...formData, short_code: e.target.value.toUpperCase() })} placeholder="e.g., ACME" maxLength={10} required />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="customer-email">Email</Label>
                  <Input id="customer-email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="contact@example.com" maxLength={255} />
                </div>
              </div>
              <div className="border-t pt-4">
                <h4 className="font-medium mb-3">Address</h4>
                <AddressFields prefix="customer" values={formData} onChange={(field, value) => setFormData({ ...formData, [field]: value })} />
              </div>
              <div className="border-t pt-4">
                <h4 className="font-medium mb-3">Billing Requirements</h4>
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <Checkbox id="requires_tender_id" checked={formData.requires_tender_id} onCheckedChange={(checked) => setFormData({ ...formData, requires_tender_id: checked === true })} />
                    <Label htmlFor="requires_tender_id" className="font-normal">Requires Tender ID</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox id="requires_bol" checked={formData.requires_bol} onCheckedChange={(checked) => setFormData({ ...formData, requires_bol: checked === true })} />
                    <Label htmlFor="requires_bol" className="font-normal">Requires Bill of Lading (BOL)</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox id="requires_tender" checked={formData.requires_tender} onCheckedChange={(checked) => setFormData({ ...formData, requires_tender: checked === true })} />
                    <Label htmlFor="requires_tender" className="font-normal">Requires Tender</Label>
                  </div>
                </div>
              </div>
              <div className="border-t pt-4">
                <h4 className="font-medium mb-3">Operations</h4>
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <Checkbox id="authorized_nvo" checked={formData.authorized_nvo} onCheckedChange={(checked) => setFormData({ ...formData, authorized_nvo: checked === true })} />
                    <Label htmlFor="authorized_nvo" className="font-normal">Authorized NVO</Label>
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
                <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>{editingCustomer ? "Update" : "Create"}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      }
    >
      <div className="space-y-4 animate-fade-in">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1 flex-wrap">
            <SearchChip query={searchQuery} onClear={() => setSearchQuery("")} />
            {activeFilters.includes("billing") && (
              <ChipFilter
                label="Billing requirements"
                icon={<FileText className="h-3.5 w-3.5" />}
                multiSelect
                value={billingReqFilter}
                onChange={(v) => setBillingReqFilter(Array.isArray(v) ? v : [v])}
                onRemove={() => removeFilter("billing")}
                open={openFilterKey === "billing"}
                onOpenChange={(o) => handleOpenChange("billing", o, billingReqFilter)}
                options={[
                  { value: "tender", label: "Load Tender" },
                  { value: "tender_id", label: "Tender ID" },
                  { value: "bol", label: "Bill of Lading" },
                ] as ChipFilterOption[]}
              />
            )}
            {activeFilters.includes("operations") && (
              <ChipFilter
                label="Operations"
                icon={<Briefcase className="h-3.5 w-3.5" />}
                multiSelect
                value={operationsFilter}
                onChange={(v) => setOperationsFilter(Array.isArray(v) ? v : [v])}
                onRemove={() => removeFilter("operations")}
                open={openFilterKey === "operations"}
                onOpenChange={(o) => handleOpenChange("operations", o, operationsFilter)}
                options={[
                  { value: "nvo", label: "Authorized NVO" },
                ] as ChipFilterOption[]}
              />
            )}
            <AddFilterMenu
              categories={availableCategories}
              onSelect={addFilter}
              iconOnly={activeFilters.length > 0 || (!!searchQuery && searchQuery.trim().length > 0)}
            />
            {(activeFilters.length > 0) && (
              <Button
                variant="ghost"
                size="sm"
                className="text-sm text-muted-foreground hover:text-foreground"
                onClick={() => {
                  setActiveFilters([]);
                  setOpenFilterKey(null);
                  ignoreAutoCloseRef.current = null;
                  setBillingReqFilter(["all"]);
                  setOperationsFilter(["all"]);
                }}
              >
                Reset filters
              </Button>
            )}
          </div>
          <div className="ml-auto flex items-center gap-3">
            <span className="text-sm text-muted-foreground whitespace-nowrap">Showing {filteredCustomers?.length ?? 0} customer{(filteredCustomers?.length ?? 0) === 1 ? "" : "s"}</span>
            <ExpandingSearch value={searchQuery} onChange={setSearchQuery} placeholder="Search customers..." />
          </div>
        </div>
        <div>
          <BulkActionBar count={count} onClear={clear} entityLabel="customer">
            <Button
              variant="ghost"
              size="sm"
              className="text-primary-foreground hover:bg-primary-foreground/20 hover:text-primary-foreground"
              onClick={() => bulkDeleteMutation.mutate(Array.from(selectedIds))}
              disabled={bulkDeleteMutation.isPending}
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Delete
            </Button>
          </BulkActionBar>
          <div className={`rounded-lg border bg-card ${count > 0 ? "rounded-t-none border-t-0" : ""}`}>
          <ResponsiveTableShell
            cards={
              <div className="flex flex-col gap-3 p-3">
                {isLoading ? (
                  <div className="text-center py-8 text-muted-foreground">Loading...</div>
                ) : pagedCustomers.length ? (
                  pagedCustomers.map((customer) => (
                    <CustomerCard
                      key={customer.id}
                      customer={customer}
                      isChecked={selectedIds.has(customer.id)}
                      onToggle={toggle}
                      onOpen={openCustomer}
                      onEdit={handleEdit}
                      onDelete={(id) => deleteMutation.mutate(id)}
                      templateCount={templateCounts?.[customer.id] ?? 0}
                    />
                  ))
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    {searchQuery.trim() ? "No customers match your filter criteria" : "No customers found"}
                  </div>
                )}
              </div>
            }
            table={
          <Table stickyColumns={2}>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[40px]">
                  <Checkbox
                    checked={allSelected}
                    onCheckedChange={toggleAll}
                    aria-label="Select all"
                    {...(someSelected ? { "data-state": "indeterminate" } : {})}
                  />
                </TableHead>
                <TableHead>Organization</TableHead>
                <TableHead>Short Code</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Billing Requirements</TableHead>
                <TableHead>Operations</TableHead>
                <TableHead>Address</TableHead>
                <TableHead className="w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8">Loading...</TableCell></TableRow>
              ) : pagedCustomers.length ? (
                pagedCustomers.map((customer) => {
                  const billingReqs = [
                    customer.requires_tender_id && "Tender ID",
                    customer.requires_bol && "BOL",
                    customer.requires_tender && "Tender",
                  ].filter(Boolean);
                  return (
                      <TableRow key={customer.id} className="cursor-pointer" onClick={() => openCustomer(customer.id)}>
                        <TableCell className="w-[40px]" onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedIds.has(customer.id)}
                            onCheckedChange={() => toggle(customer.id)}
                          />
                        </TableCell>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            {customer.organization_name}
                            {(templateCounts?.[customer.id] ?? 0) > 0 && (
                              <span className="px-1.5 py-0.5 text-xs rounded-full bg-muted text-muted-foreground font-normal">
                                {templateCounts[customer.id]} {templateCounts[customer.id] === 1 ? "order template" : "order templates"}
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell><code className="bg-muted px-2 py-1 rounded text-sm">{customer.short_code}</code></TableCell>
                        <TableCell>
                          {customer.email ? (
                            <a href={`mailto:${customer.email}`} className="text-primary hover:underline text-sm" onClick={(e) => e.stopPropagation()}>{customer.email}</a>
                          ) : <span className="text-muted-foreground text-sm">—</span>}
                        </TableCell>
                        <TableCell>
                          {billingReqs.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {billingReqs.map((req) => (
                                <span key={req as string} className="status-badge status-booked">
                                  {req}
                                </span>
                              ))}
                            </div>
                          ) : <span className="text-muted-foreground text-sm">None</span>}
                        </TableCell>
                        <TableCell>
                          {customer.authorized_nvo ? (
                            <span className="status-badge status-booked">NVO</span>
                          ) : <span className="text-muted-foreground text-sm">—</span>}
                        </TableCell>
                        <TableCell className="text-muted-foreground">{formatAddress(customer)}</TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" onClick={() => handleEdit(customer)}><Pencil className="h-4 w-4" /></Button>
                            <ConfirmDeleteButton
                              onConfirm={() => deleteMutation.mutate(customer.id)}
                              entityName="customer"
                              entityLabel={customer.organization_name}
                            />
                          </div>
                        </TableCell>
                      </TableRow>
                  );
                })
              ) : (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">{searchQuery.trim() ? "No customers match your filter criteria" : "No customers found"}</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
            }
          />
          <TablePagination
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            totalPages={totalPages}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        </div>
        </div>
      </div>

    </AppLayout>
  );
}
